import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VerifiedIcon from "@mui/icons-material/Verified";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import RawClipGrid from "../../components/RawClipGrid";
import { rawIdsOf } from "./batchItems";

const INDIGO = "#4338ca";
const toggleSx = {
  height: 26,
  px: 1,
  gap: 0.5,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 11.5,
  color: "#475569",
  borderColor: "#cbd5e1",
  "&.Mui-selected, &.Mui-selected:hover": { color: INDIGO, bgcolor: alpha(INDIGO, 0.08), borderColor: alpha(INDIGO, 0.5) },
  "&.Mui-focusVisible": { outline: `2px solid ${INDIGO}`, outlineOffset: 1 },
  "&.Mui-disabled": { color: "#94a3b8" },
};

/**
 * "Video: Edited | Raw clips" for the open tab's WGID. Picking the other side
 * (or Raw clips again, to change the clips) opens a panel: the clip grid for
 * raw, the scope (every platform of this WGID in this brand, or this one), and
 * Apply. The dialog decides what that costs and confirms it.
 *
 * `siblings`: this WGID's tabs in this brand, `[{platform, config}]`.
 */
export default function VideoChoiceControl({ item, siblings, finalAvailable, rawVideos, onApply, onPanelChange }) {
  const anchorRef = useRef(null);
  const [panel, setPanel] = useState(null); // null | "final" | "raw"
  const [clips, setClips] = useState({ status: "loading", list: [] });
  const [selected, setSelected] = useState([]);
  const [scope, setScope] = useState("all");

  useEffect(() => {
    let live = true;
    rawVideos(item.wgid)
      .then((list) => live && setClips({ status: "ready", list }))
      .catch(() => live && setClips({ status: "error", list: [] }));
    return () => {
      live = false;
    };
  }, [item.wgid, rawVideos]);

  useEffect(() => onPanelChange(Boolean(panel)), [panel, onPanelChange]);
  useEffect(() => () => onPanelChange(false), [onPanelChange]);

  const currentIds = rawIdsOf(item.config);
  const isRaw = currentIds.length > 0;
  const rawAvailable = clips.status === "ready" && clips.list.length > 0;
  const others = siblings.filter((s) => s.platform !== item.platform);
  const differing = others.filter((s) => rawIdsOf(s.config).join(",") !== currentIds.join(","));
  const mixedText = differing.map((s) => `${s.platform} uses ${rawIdsOf(s.config).length > 0 ? "raw clips" : "the edited video"}`).join(", ");

  const openPanel = (kind) => {
    // Pre-tick this tab's clips, matched to the list's own id types. Coming
    // from Edited (none picked yet), every clip starts ticked (user request
    // 2026-10-06) — untick the ones to leave out.
    const mine = clips.list.filter((c) => currentIds.includes(String(c.id))).map((c) => c.id);
    setSelected(kind === "raw" ? (mine.length > 0 ? mine : clips.list.map((c) => c.id)) : []);
    setScope("all");
    setPanel(kind);
  };

  const apply = () => {
    const ids = panel === "raw" ? clips.list.filter((c) => selected.includes(c.id)).map((c) => c.id) : [];
    setPanel(null);
    onApply({ ids, scope });
  };

  const rawTip =
    clips.status === "loading"
      ? "Checking for raw clips…"
      : clips.status === "error"
        ? "Could not load this video's raw clips"
        : !rawAvailable
          ? "This video has no raw clips"
          : isRaw
            ? "Change the raw clips"
            : "";
  const finalTip = finalAvailable ? "" : "This video has no edited file to edit";

  return (
    <>
      <Box ref={anchorRef} display="flex" alignItems="center" gap={0.75}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" id={`video-choice-${item.key}`} sx={{ fontSize: 11 }}>
          Video
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={isRaw ? "raw" : "final"} aria-labelledby={`video-choice-${item.key}`}>
          <Tooltip title={finalTip}>
            <span>
              <ToggleButton value="final" disabled={isRaw && !finalAvailable} onClick={() => isRaw && openPanel("final")} sx={{ ...toggleSx, borderRadius: "6px 0 0 6px" }}>
                <VerifiedIcon sx={{ fontSize: 14 }} aria-hidden />
                Edited
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip title={rawTip}>
            <span>
              <ToggleButton value="raw" disabled={!rawAvailable} onClick={() => openPanel("raw")} sx={{ ...toggleSx, borderRadius: "0 6px 6px 0", ml: "-1px" }}>
                {clips.status === "loading" ? <CircularProgress size={12} aria-hidden /> : <MovieCreationOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />}
                Raw clips{isRaw ? ` · ${currentIds.length}` : ""}
              </ToggleButton>
            </span>
          </Tooltip>
        </ToggleButtonGroup>
        {differing.length > 0 && (
          <Tooltip title={`Mixed: ${mixedText}`}>
            <Typography variant="caption" tabIndex={0} aria-label={`Mixed: ${mixedText}`} sx={{ color: "#b45309", fontWeight: 700, cursor: "help", borderBottom: "1px dotted #b45309" }}>
              Mixed
            </Typography>
          </Tooltip>
        )}
      </Box>

      <Popover
        open={Boolean(panel)}
        anchorEl={anchorRef.current}
        onClose={() => setPanel(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 1, p: 2, width: 560, maxWidth: "calc(100vw - 32px)", borderRadius: 2 } }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {panel === "raw" ? `Edit ${item.wgid} from raw clips` : `Edit ${item.wgid} from the edited video`}
        </Typography>

        {panel === "raw" && (
          <Box mb={1.5} sx={{ maxHeight: 280, overflowY: "auto", pt: 1, pr: 1 }}>
            <RawClipGrid
              rawVideos={clips.list}
              selectedIds={selected}
              onToggle={(id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
            />
          </Box>
        )}
        {panel === "final" && (
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            The editor opens the video's edited file instead of raw clips.
          </Typography>
        )}

        <Typography variant="caption" fontWeight={700} color="text.secondary" component="div" id={`video-scope-${item.key}`}>
          Apply to
        </Typography>
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)} aria-labelledby={`video-scope-${item.key}`}>
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label={<Typography variant="body2">All platforms of {item.wgid} in {item.brandName} ({siblings.length})</Typography>}
          />
          <FormControlLabel value="one" control={<Radio size="small" />} label={<Typography variant="body2">This platform only ({item.platform})</Typography>} />
        </RadioGroup>

        {others.length === 0 && scope === "all" && (
          <Alert severity="info" sx={{ mt: 1, py: 0, fontSize: 12.5 }}>
            {item.platform} is the only Edited platform of this video.
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button onClick={() => setPanel(null)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={apply}
            disabled={panel === "raw" && selected.length === 0}
            sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, #312e81, ${INDIGO})` }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}
